import React, {useEffect, useMemo, useState} from 'react';
import NicknameStep from './nickname-step/nickname-step';
import ConnectionStep from './connection-step/connection-step';
import PolicyStep from './policy-step/policy-step';
import Wizard from './wizard';
import DDLogo from '@svg/dd-logo.svg?react';
import {useLocation, useParams} from 'react-router-dom';
import { useNavigate } from 'src/utils/use-navigate';
import {observer} from 'mobx-react-lite';
import {useStores} from '../../store';
import usePromiseFeedback from '../../shared/promise-feedback/usePromiseFeedback';
import Loader from '../../shared/loader/loader';
import isNumeric from 'antd/es/_util/isNumeric';
import {LevelName, PolicyMode} from '../../../utils/enums';
import {ServiceDto} from '../../../dtos/customer.dto';
import {DUPLICATE_SERVICE_NAME, ILLEGAL_CHARACTERS_MESSAGE, validateString} from '../../../utils/validationUtils';
import {EventType} from '../../../dtos/analytics.dto';
import './wizard-page.scss';
import {ChatBot} from '../../shared/chat-bot/chat-bot';
import {isChatBotEnable} from '@src/utils/chat-bot-utils';

const WizardPage: React.FC = observer(() => {
    const { state } = useLocation();
    const { initialData: initialDataString, initialStep } = state ?? {};
    const initialData = initialDataString ? JSON.parse(initialDataString) : {};
    const { customerStore, userStore } = useStores();
    const [promiseFeedbackFactory] = usePromiseFeedback();
    const [isReady, setIsReady] = useState(false);
    const { customerId } = useParams<{ customerId: string }>();
    const navigate = useNavigate();

    useEffect(() => {
        if (!customerStore.isLoading && !customerStore.data) {
            promiseFeedbackFactory({
                promise: customerStore.load(),
                notifObj: { show: false }
            });
        }
    }, []);

    useEffect(() => {
        if (customerStore.data && isNumeric(customerId)) {
            void customerStore.loadSelectedCustomer(Number(customerId)).then(() => setIsReady(true));
        }
    }, [customerStore.data, customerId]);

    const handleComplete = async (wizardData: any) => {
        if (!customerStore.selectedCustomer?.services) return;

        if (wizardData.policy) {
            customerStore.sendGeneralEvent(EventType.LinkClicked, { button: 'next',
                location: 'pickAPolicyPage', policy_mode: wizardData.policy });
        }

        if (wizardData.connection) {
            await customerStore.setServiceNameAndPolicyMode(Number(customerId),
                wizardData.connection.serviceId,
                wizardData.nickname || wizardData.connection.serviceName || wizardData.connection.adiCircuitId,
                wizardData.policy || PolicyMode.CUSTOM_PROTECTION
            );
        }

        if (customerStore.isSingleConnection) {
            const singleService = customerStore.selectedCustomer.services[0];
            navigate(`/${customerId}/${singleService.serviceId}/dashboard`);
        } else {
            navigate(`/${customerId}/connections`,
                { state: { isWizardComplete: true, wizardConnectionId: wizardData.connection?.serviceId }});
        }
    };

    const nonBronzeConnections = useMemo(() =>
            customerStore.selectedCustomer?.services.filter(
                connection => connection.serviceLevel !== LevelName.BRONZE
            )
        ,[customerStore.selectedCustomer?.services]);

    const steps = useMemo(() => [
        {
            component: ConnectionStep,
            validate: (data: { connection?: ServiceDto }) => ({ isValid: !!data.connection }),
            extraProps: { connections: nonBronzeConnections },
            fieldName: 'connection' as const,
            eventName: 'setupFirstConnectionPage',
        },
        {
            component: NicknameStep,
            validate: (data: { nickname?: string; connection?: ServiceDto }) => {
                if (!data.nickname) return { isValid: true };

                const trimmedNickname = data.nickname.trim();
                const isFormatError = validateString(trimmedNickname, false, ['@', '#', '$', '%', '&', '*', '+', '/']);
                if (isFormatError) {
                    return { isValid: false, error: ILLEGAL_CHARACTERS_MESSAGE };
                }

                const isUnique = !nonBronzeConnections?.some((conn) =>
                        conn.serviceId !== data.connection?.serviceId &&
                        (conn.serviceName === data.nickname || conn.adiCircuitId === data.nickname)
                );

                if (!isUnique) {
                    return { isValid: false, error: DUPLICATE_SERVICE_NAME };
                }
                return { isValid: true };
            },
            fieldName: 'nickname' as const,
            eventName: 'connectionNicknamePage',
        },
        {
            component: PolicyStep,
            validate: (data: { policy?: string }) => ({ isValid: !!data.policy }),
            fieldName: 'policy' as const,
            eventName: 'pickAPolicyPage',
            eventProperty: 'policy_mode',
            nextLabel: 'Select a policy'
        },
    ], [nonBronzeConnections]);

    return (
        <div className="wizard-page">
            <div className="wizard-page-header">
                <DDLogo className="app-logo" id="logo"/>
            </div>
            {!isReady ? <Loader type="full-screen" /> :
                <Wizard
                    steps={steps}
                    initialStep={initialStep}
                    initialData={initialData}
                    onComplete={handleComplete}
                    sendEvent={(eventType: EventType, extraInfo?: any) => customerStore.sendGeneralEvent(eventType, extraInfo)}
                    isDisableNext={userStore.isReadOnlyUser}
                />}
            {isChatBotEnable(customerStore, userStore) && <ChatBot/>}
        </div>
    );
});

export default WizardPage;